import { useMeasure } from 'react-use'
import classNames from 'classnames'

import Container from '@/components/base/Container'
import Motion from '@/components/base/Motion'
import Typography from '@/components/base/Typography'

import { timelineRevealAnimation } from './animations'
import { Section } from '@/types'

type TimelineProps = Section &
  Partial<{
    steps: {
      title: string
      overline: string
      paragraph: string
    }[]
  }>

const Timeline = ({ steps, ...props }: TimelineProps) => {
  const [last, { height: lastHeight }] = useMeasure<HTMLDivElement>()

  return (
    <Motion animation={timelineRevealAnimation}>
      {(motionRef) => (
        <section ref={motionRef} {...props}>
          <Container>
            <div className="m-auto max-w-[70rem]">
              <div className="relative flex flex-col space-y-[1rem]">
                <span
                  className={classNames(
                    'absolute bottom-0 left-1/2 top-0 block w-[.4rem] -translate-x-1/2 rounded-full bg-white',
                    'before:absolute before:-top-[4rem] before:h-[4rem] before:w-full',
                    'before:bg-gradient-to-t before:from-white before:to-transparent',
                    'hidden md:block'
                  )}
                  style={{ bottom: lastHeight / 2 }}
                  data-motion-line
                />
                {steps?.map(({ overline, title, paragraph }, index) => {
                  const isLast = index === steps.length - 1
                  return (
                    <div
                      ref={isLast ? last : null}
                      key={`step-${index}`}
                      className={classNames('relative')}
                    >
                      <span
                        className={classNames(
                          {
                            'absolute right-1/2 top-1/2 h-[3rem] w-[3rem] -translate-y-1/2 translate-x-1/2 rounded-full bg-white':
                              index % 2 === 0,
                            'absolute left-1/2 top-1/2 h-[3rem] w-[3rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white':
                              index % 2 === 1
                          },
                          'hidden md:block',
                          'hidden md:block'
                        )}
                        data-motion-step-dot
                      />
                      <div
                        className={classNames(
                          'w-full rounded-xxs p-sm shadow-3xl md:max-w-[28.75rem]',
                          {
                            'mr-auto': index % 2 === 0,
                            'ml-auto': index % 2 === 1
                          },
                          {
                            'bg-white': !isLast,
                            'bg-blue-soft': isLast
                          }
                        )}
                        data-motion-step-card
                      >
                        <div className="grid gap-y-xs">
                          {overline ? (
                            <Typography
                              variant="caption"
                              className={classNames('uppercase', {
                                'text-blue-soft': !isLast,
                                'text-white': isLast
                              })}
                            >
                              {overline}
                            </Typography>
                          ) : null}

                          {title ? (
                            <Typography
                              variant="h6"
                              component="h6"
                              className={classNames({
                                '[&_em]:text-blue-soft text-dark': !isLast,
                                'text-white': isLast
                              })}
                            >
                              {title}
                            </Typography>
                          ) : null}

                          {paragraph ? (
                            <Typography
                              variant="body"
                              paragraph
                              className={classNames({
                                'text-neutral-20': !isLast,
                                'text-white': isLast
                              })}
                            >
                              {paragraph}
                            </Typography>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </Container>
        </section>
      )}
    </Motion>
  )
}

export default Timeline
